import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink, MousePointerClick } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/motion/Reveal";
import { CTASection } from "@/components/CTASection";
import { DEMOS } from "@/data/content";

const BASE_W = 1440;
const BASE_H = 900;

const DemoPreview = ({ url }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / BASE_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="relative w-full overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
      <iframe
        src={url}
        title="Website demo preview"
        loading="lazy"
        scrolling="no"
        tabIndex={-1}
        aria-hidden="true"
        className="absolute left-0 top-0"
        style={{
          width: `${BASE_W}px`,
          height: `${BASE_H}px`,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          border: 0,
          pointerEvents: "none",
        }}
      />
    </div>
  );
};

export default function Demos() {
  return (
    <div data-testid="demos-page">
      <PageHeader
        overline="Live Demos"
        titleLines={["Real work.", "Live and", "clickable."]}
        subtitle="Explore live websites we've built for real businesses. Click any demo to open the full experience in a new tab."
      />

      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8">
          <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
            {DEMOS.map((d, i) => (
              <Reveal key={d.name} delay={i * 0.08} className={DEMOS.length === 1 ? "md:col-span-2" : ""}>
                <div
                  data-testid={`demo-card-${i}`}
                  className="group overflow-hidden rounded-3xl border border-black/10 bg-paper transition-shadow duration-300 hover:shadow-2xl hover:shadow-ink/10"
                >
                  <a
                    href={d.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`demo-preview-link-${i}`}
                    className="relative block overflow-hidden border-b border-black/10"
                  >
                    <DemoPreview url={d.url} />
                    <div className="absolute inset-0 flex items-center justify-center bg-ink/0 opacity-0 transition-all duration-300 group-hover:bg-ink/30 group-hover:opacity-100">
                      <span className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-bold text-ink shadow-lg">
                        <MousePointerClick className="h-4 w-4 text-brand-dark" /> Open Live Site
                      </span>
                    </div>
                    <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.15em] text-brand-dark backdrop-blur">
                      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-brand" /> Live
                    </span>
                  </a>

                  <div className="p-7 sm:p-9">
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-brand-dark">
                      {d.category}
                    </p>
                    <h3 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                      {d.name}
                    </h3>
                    <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-ink/60">
                      {d.desc}
                    </p>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {d.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full border border-black/10 bg-white px-3 py-1 text-xs font-semibold text-ink/70"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`demo-visit-${i}`}
                      className="group/btn mt-7 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
                    >
                      View Live Demo
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <div className="mt-14 flex flex-col items-center justify-between gap-6 rounded-3xl border border-black/10 bg-ink p-10 text-center text-white sm:flex-row sm:text-left">
              <div>
                <h3 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
                  Want a site like this for your business?
                </h3>
                <p className="mt-2 text-white/60">
                  Let's build something that wins — for today, and the future.
                </p>
              </div>
              <a
                href="/contact"
                className="inline-flex shrink-0 items-center gap-2 rounded-full bg-brand px-7 py-4 font-bold text-ink transition-transform duration-300 hover:-translate-y-0.5 hover:bg-brand-dark hover:text-white"
              >
                Get a Free Quote <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
